/**
 * Common (customer/product group) defaults for SetupV2 onboarding.
 * First item from e-conomic API when empty. Wizard no longer has a Common step —
 * fields are seeded on Invoice (editable) or silently on Payment/Subscription.
 */
export interface CommonDefaultOptions {
    customerGroups?: Array<{customerGroupNumber?: string | number}>;
    paymentTerms?: Array<{paymentTermsNumber?: string | number}>;
    productGroups?: Array<{productGroupNumber?: string | number}>;
}

let commonOptionsCache: CommonDefaultOptions = {};

export const setCommonDefaultOptionsCache = (options: CommonDefaultOptions): void => {
    commonOptionsCache = {
        customerGroups: options.customerGroups || [],
        paymentTerms: options.paymentTerms || [],
        productGroups: options.productGroups || [],
    };
};

const isEmpty = (value: unknown): boolean =>
    value === null || value === undefined || value === '';

export const COMMON_DEFAULT_KEYS = [
    'economic_customer_group_number',
    'economic_customer_payment_terms_number',
    'economic_product_group_number',
] as const;

export const applyCommonSetupDefaults = <T extends Record<string, any>>(
    values: T,
    options?: CommonDefaultOptions,
): T => {
    const opts = options || commonOptionsCache;
    const next: T = {...values};

    const firstCustomerGroup = opts.customerGroups?.[0]?.customerGroupNumber;
    if (isEmpty(next.economic_customer_group_number) && firstCustomerGroup != null && firstCustomerGroup !== '') {
        (next as any).economic_customer_group_number = firstCustomerGroup;
    }

    const firstPaymentTerm = opts.paymentTerms?.[0]?.paymentTermsNumber;
    if (
        isEmpty(next.economic_customer_payment_terms_number)
        && firstPaymentTerm != null
        && firstPaymentTerm !== ''
    ) {
        (next as any).economic_customer_payment_terms_number = firstPaymentTerm;
    }

    const firstProductGroup = opts.productGroups?.[0]?.productGroupNumber;
    if (isEmpty(next.economic_product_group_number) && firstProductGroup != null && firstProductGroup !== '') {
        (next as any).economic_product_group_number = firstProductGroup;
    }

    return next;
};

/** Apply common field defaults onto formik when values differ. */
export const seedCommonDefaultsToFormik = (
    formik: {values: Record<string, any>; setFieldValue: (key: string, value: any) => void},
    options?: CommonDefaultOptions,
): void => {
    const next = applyCommonSetupDefaults(formik.values, options);
    COMMON_DEFAULT_KEYS.forEach((key) => {
        if (next[key] !== formik.values[key]) {
            formik.setFieldValue(key, next[key]);
        }
    });
};
